import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase, signIn, signUp, signOut, getSession } from './supabaseClient';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://faresight.flywithdata.com/api/v1';

// API Client with token management
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Supabase token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('faresight_supabase_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;  // Supabase uses Bearer, not Token
  }
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('faresight_supabase_token');
      await signOut();  // Sign out from Supabase
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// ==================== Landing Page Component ====================
function LandingPage({ onGetStarted }) {
  const [demoData, setDemoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch real-time demo prediction from backend
    const fetchDemoData = async () => {
      try {
        console.log('[FareSight] Fetching demo data from:', `${API_BASE_URL}/public/demo-prediction`);
        const response = await axios.get(`${API_BASE_URL}/public/demo-prediction`);
        console.log('[FareSight] Demo data received:', response.data);
        setDemoData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('[FareSight] Failed to fetch demo data:', error);
        console.error('[FareSight] Error details:', error.response?.data || error.message);
        // Use fallback static data if API fails
        setDemoData(null);
        setLoading(false);
      }
    };

    fetchDemoData();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #0b1120, #172554, #0b1120)' }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Navigation */}
        <nav className="relative z-10 px-6 py-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-2xl font-bold">✈</span>
              </div>
              <span className="text-white text-2xl font-bold">FareSight</span>
            </div>
            <button
              onClick={onGetStarted}
              className="px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Sign In
            </button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 text-center">
          <h1 className="text-6xl md:text-8xl font-black text-white mb-8 leading-tight">
            Predict Flight Prices
            <span className="block text-amber-500 mt-4">
              48 Hours in Advance
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            AI-powered price drop predictions for B2B travel resellers.
            <span className="block mt-2">Never miss the optimal booking moment.</span>
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={onGetStarted}
              className="px-10 py-5 bg-amber-500 text-white rounded-lg font-bold text-lg hover:bg-amber-600 transition-all shadow-2xl"
            >
              Get Started Free
            </button>
            <button
              onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
              className="px-10 py-5 bg-gray-800 text-white rounded-lg font-bold text-lg hover:bg-gray-700 transition-all border-2 border-gray-700"
            >
              See Live Demo
            </button>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
              <div className="text-6xl font-black text-amber-500">92%</div>
              <div className="text-gray-400 mt-3 text-lg font-semibold">Prediction Accuracy</div>
            </div>
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
              <div className="text-6xl font-black text-amber-500">48h</div>
              <div className="text-gray-400 mt-3 text-lg font-semibold">Price Forecast</div>
            </div>
            <div className="bg-gray-900 rounded-lg p-8 border border-gray-800">
              <div className="text-6xl font-black text-amber-500">24/7</div>
              <div className="text-gray-400 mt-3 text-lg font-semibold">Auto Monitoring</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-black text-center text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 text-center mb-20 max-w-2xl mx-auto">
            Three simple steps to start saving on flight bookings
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Track Routes</h3>
              <p className="text-gray-600">
                Add flight routes you want to monitor. Set departure and return dates for automatic tracking.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Predictions</h3>
              <p className="text-gray-600">
                Our ARIMA AI model analyzes historical data and predicts price drops in the next 48 hours.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Book Smart</h3>
              <p className="text-gray-600">
                Get alerts when prices are predicted to drop 10%+ and book at the optimal moment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Section */}
      <div id="demo" className="py-28" style={{ background: '#0f172a' }}>
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-black text-center text-white mb-6">
            Live Demo
          </h2>
          <p className="text-xl text-gray-400 text-center mb-16 max-w-2xl mx-auto">
            Real-time AI prediction engine in action
          </p>

          {/* Demo Screenshot/Preview */}
          <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-800">
            <div className="bg-gray-800 px-6 py-4 flex items-center space-x-2 border-b border-gray-700">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-400 text-sm ml-4">faresight.flywithdata.com</span>
            </div>

            <div className="p-8">
              {loading ? (
                /* Loading State */
                <div className="flex items-center justify-center py-20">
                  <div className="animate-pulse text-gray-400 text-lg">
                    Loading live prediction data...
                  </div>
                </div>
              ) : demoData ? (
                /* Real-time Demo Data */
                <>
                  {/* Live Dashboard */}
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <h3 className="text-2xl font-bold text-gray-300">Tracked Routes</h3>
                      <span className="px-2 py-1 bg-green-900 text-green-400 text-xs font-semibold rounded-full border border-green-700">
                        LIVE
                      </span>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-amber-500">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="text-lg font-semibold text-white">
                            {demoData.route.origin} → {demoData.route.destination}
                          </div>
                          <div className="text-sm text-gray-400">
                            Seoul to Los Angeles • {demoData.route.departure_date} - {demoData.route.return_date}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${
                            demoData.prediction.alert_needed ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            {demoData.prediction.alert_needed ? '↓' : '→'} {demoData.prediction.drop_percent.toFixed(1)}%
                          </div>
                          <div className={`text-sm font-semibold ${
                            demoData.prediction.alert_needed ? 'text-green-400' : 'text-gray-400'
                          }`}>
                            {demoData.recommendation}
                          </div>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            demoData.prediction.alert_needed ? 'bg-green-500' : 'bg-amber-500'
                          }`}
                          style={{ width: `${Math.min(demoData.prediction.drop_percent * 10, 100)}%` }}
                        ></div>
                      </div>
                      <div className="mt-2 text-sm text-gray-300">
                        Current: ${demoData.prediction.current_price} → Predicted Low: ${demoData.prediction.predicted_lowest_price}
                        {demoData.prediction.savings_amount > 0 && (
                          <span className="text-green-400 font-semibold">
                            {' '}(Save ${demoData.prediction.savings_amount})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Live Line Chart with Improved Date Display */}
                  <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-amber-500">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-white">Price Trend & 48h AI Prediction</h4>
                      <span className="text-xs text-gray-400">Live Data • Updated Daily</span>
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={(() => {
                        // Convert chart data to include actual dates
                        const today = new Date();
                        return demoData.chart_data.current_year.map(d => {
                          const date = new Date(today);
                          date.setDate(date.getDate() + d.day);
                          return {
                            ...d,
                            fullDate: d.day === 0 ? 'Today' :
                                     d.day === 1 ? 'Tomorrow' :
                                     date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                            displayDate: `${date.getMonth() + 1}/${date.getDate()}`,
                            isToday: d.day === 0,
                            isPrediction: d.type === 'predicted'
                          };
                        });
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="fullDate"
                          stroke="#9ca3af"
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          stroke="#9ca3af"
                          tick={{ fontSize: 11, fill: '#9ca3af' }}
                          label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', fill: '#9ca3af' }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                          labelFormatter={(label) => {
                            const point = demoData.chart_data.current_year.find((d, i) => {
                              const date = new Date();
                              date.setDate(date.getDate() + d.day);
                              const formatted = d.day === 0 ? 'Today' :
                                               d.day === 1 ? 'Tomorrow' :
                                               date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                              return formatted === label;
                            });
                            if (point?.isToday) return '📅 Today (Current Price)';
                            if (point?.isPrediction) return `🔮 ${label} (AI Prediction)`;
                            return `📅 ${label}`;
                          }}
                          formatter={(value, name) => [`$${value.toFixed(2)}`, name === 'price' ? 'Price' : 'Predicted']}
                        />
                        <Legend wrapperStyle={{ paddingTop: '10px' }} />

                        {/* Reference line for Today */}
                        <ReferenceLine
                          x="Today"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          label={{
                            value: 'TODAY',
                            position: 'top',
                            fill: '#f59e0b',
                            fontSize: 10,
                            fontWeight: 'bold'
                          }}
                        />

                        {/* Historical price line */}
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            return (
                              <circle
                                cx={cx}
                                cy={cy}
                                r={payload.isPrediction ? 6 : payload.isToday ? 5 : 3}
                                fill={payload.isPrediction ? '#10b981' : '#f59e0b'}
                                stroke="#1f2937"
                                strokeWidth={2}
                              />
                            );
                          }}
                          name="Market Price"
                        />

                        {/* Prediction line segment */}
                        <Line
                          type="monotone"
                          dataKey={(d) => d.isPrediction ? d.price : null}
                          stroke="#10b981"
                          strokeWidth={4}
                          strokeDasharray="5 3"
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            if (!payload.isPrediction) return null;
                            return (
                              <circle
                                cx={cx}
                                cy={cy}
                                r={6}
                                fill="#10b981"
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            );
                          }}
                          connectNulls={false}
                          name="🔮 AI Prediction"
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    <svg viewBox="0 0 600 200" className="w-full h-48 hidden">
                      {/* Grid lines */}
                      <g stroke="#374151" strokeWidth="1">
                        {[0, 50, 100, 150, 200].map(y => (
                          <line key={y} x1="40" y1={y} x2="580" y2={y} strokeDasharray="2,2" />
                        ))}
                      </g>

                      {(() => {
                        const currentYear = demoData.chart_data.current_year;
                        const lastYear = demoData.chart_data.last_year;

                        // Find price range
                        const allPrices = [
                          ...currentYear.map(d => d.price),
                          ...lastYear.filter(d => d.price !== null).map(d => d.price)
                        ];
                        const minPrice = Math.min(...allPrices);
                        const maxPrice = Math.max(...allPrices);
                        const priceRange = maxPrice - minPrice || 100;

                        // Scale functions
                        const xScale = (day) => 40 + ((day + 13) / 15) * 540;
                        const yScale = (price) => 180 - ((price - minPrice) / priceRange) * 160;

                        // Generate path for current year
                        const currentYearPath = currentYear.map((d, i) =>
                          `${i === 0 ? 'M' : 'L'} ${xScale(d.day)} ${yScale(d.price)}`
                        ).join(' ');

                        // Generate path for last year
                        const lastYearPath = lastYear
                          .filter(d => d.price !== null)
                          .map((d, i) =>
                            `${i === 0 ? 'M' : 'L'} ${xScale(d.day)} ${yScale(d.price)}`
                          ).join(' ');

                        // Find where prediction starts (day 0)
                        const predictionStartIdx = currentYear.findIndex(d => d.day === 1);

                        return (
                          <>
                            {/* Last Year Line (dashed gray) */}
                            <path
                              d={lastYearPath}
                              fill="none"
                              stroke="#6b7280"
                              strokeWidth="2"
                              strokeDasharray="5,5"
                              opacity="0.5"
                            />

                            {/* Current Year Line (solid amber -> green for prediction) */}
                            <path
                              d={currentYearPath}
                              fill="none"
                              stroke="#f59e0b"
                              strokeWidth="3"
                            />

                            {/* Prediction segment (green dashed) */}
                            {predictionStartIdx > 0 && (
                              <path
                                d={currentYear.slice(predictionStartIdx - 1).map((d, i) =>
                                  `${i === 0 ? 'M' : 'L'} ${xScale(d.day)} ${yScale(d.price)}`
                                ).join(' ')}
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="3"
                                strokeDasharray="5,3"
                              />
                            )}

                            {/* Data points */}
                            {currentYear.map((d, i) => (
                              <circle
                                key={`cy-${i}`}
                                cx={xScale(d.day)}
                                cy={yScale(d.price)}
                                r={d.type === 'predicted' ? 5 : 3}
                                fill={d.type === 'predicted' ? '#10b981' : d.day === 0 ? '#f59e0b' : '#f59e0b'}
                                stroke="#1f2937"
                                strokeWidth="2"
                              />
                            ))}

                            {/* Price labels on key points */}
                            <text x={xScale(0)} y={yScale(currentYear.find(d => d.day === 0)?.price) - 10} fontSize="10" fill="#fbbf24" fontWeight="bold" textAnchor="middle">
                              ${Math.round(currentYear.find(d => d.day === 0)?.price)}
                            </text>
                            <text x={xScale(2)} y={yScale(currentYear.find(d => d.day === 2)?.price) - 10} fontSize="10" fill="#10b981" fontWeight="bold" textAnchor="middle">
                              ${Math.round(currentYear.find(d => d.day === 2)?.price)}
                            </text>

                            {/* Y-axis labels */}
                            <text x="30" y="20" fontSize="10" fill="#9ca3af" textAnchor="end">${Math.round(maxPrice)}</text>
                            <text x="30" y="190" fontSize="10" fill="#9ca3af" textAnchor="end">${Math.round(minPrice)}</text>
                          </>
                        );
                      })()}

                      {/* Vertical line at "Today" */}
                      <line x1="382" y1="20" x2="382" y2="180" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4,4" opacity="0.5" />
                      <text x="382" y="15" fontSize="10" fill="#f59e0b" fontWeight="bold" textAnchor="middle">TODAY</text>
                    </svg>

                    <div className="mt-6 bg-gray-700 border border-gray-600 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">ℹ️</span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-200 mb-2">
                            <strong className="text-white">How to read this chart:</strong>
                          </p>
                          <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                            <li><strong className="text-amber-400">Orange solid line:</strong> Historical market prices (past 14 days)</li>
                            <li><strong className="text-amber-400">Orange dashed line:</strong> Today's current price marker</li>
                            <li><strong className="text-green-400">Green dashed line:</strong> AI-predicted prices for next 48 hours</li>
                          </ul>
                          {demoData.prediction.alert_needed && (
                            <div className="mt-3 bg-green-900/30 border border-green-500/50 rounded px-3 py-2">
                              <p className="text-sm font-semibold text-green-300">
                                💡 AI Recommendation: {demoData.recommendation}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 bg-gray-700 border border-gray-600 rounded-lg p-3 hidden">
                      <div className="flex items-start space-x-2">
                        <span className="text-amber-500 text-lg">💡</span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-300">
                            <strong className="text-white">YoY Insight:</strong> Prices are currently{' '}
                            {demoData.prediction.current_price < (demoData.chart_data.last_year[13]?.price || 0) ? (
                              <span className="text-green-400 font-semibold">
                                {Math.round(((demoData.chart_data.last_year[13]?.price - demoData.prediction.current_price) / demoData.chart_data.last_year[13]?.price) * 100)}% lower
                              </span>
                            ) : (
                              <span className="text-red-400 font-semibold">
                                {Math.round(((demoData.prediction.current_price - demoData.chart_data.last_year[13]?.price) / demoData.chart_data.last_year[13]?.price) * 100)}% higher
                              </span>
                            )}{' '}
                            than last year's same period.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-center">
                      <p className="text-xs text-gray-500">
                        Powered by ARIMA time series model • Real-time prediction
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                /* Fallback Static Data */
                <>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Tracked Routes</h3>
                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="text-lg font-semibold text-gray-900">ICN → LAX</div>
                          <div className="text-sm text-gray-600">Seoul to Los Angeles • Dec 1-15, 2025</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">↓ 12.5%</div>
                          <div className="text-sm text-green-600 font-semibold">BUY NOW</div>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{ width: '75%' }}></div>
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        Current: $890 → Predicted Low: $779 (Save $111)
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4">48-Hour Price Forecast</h4>
                    <div className="relative h-48 flex items-end space-x-2">
                      {[920, 910, 900, 890, 870, 820, 779, 785, 790].map((price, idx) => {
                        const height = ((price - 750) / 200) * 100;
                        const isPredicted = idx >= 6;
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center">
                            <div
                              className={`w-full rounded-t transition-all ${
                                isPredicted ? 'bg-green-400' : 'bg-blue-500'
                              }`}
                              style={{ height: `${height}%` }}
                            ></div>
                            <div className="text-xs text-gray-600 mt-1">${price}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-gray-600">Historical</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-green-400 rounded"></div>
                        <span className="text-gray-600">Predicted</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={onGetStarted}
              className="px-10 py-5 bg-amber-500 text-white rounded-lg font-bold text-lg hover:bg-amber-600 transition-all shadow-2xl"
            >
              Start Tracking Flights Now
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-white py-28">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-black text-center text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 text-center mb-20 max-w-2xl mx-auto">
            Choose the plan that fits your business needs
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* 7-Day Free PoC - Most Emphasized */}
            <div className="relative transform scale-105 bg-white rounded-2xl shadow-2xl border-4 border-amber-500 overflow-hidden">
              {/* Top Badge */}
              <div className="absolute top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 font-bold text-sm">
                ⭐ MOST POPULAR - START HERE
              </div>

              <div className="p-8 pt-16">
                <h3 className="text-3xl font-black text-gray-900 mb-2">7-Day Free Test</h3>
                <p className="text-gray-600 mb-6">Prove the value before you pay</p>

                <div className="mb-6">
                  <span className="text-6xl font-black text-gray-900">$0</span>
                  <span className="text-gray-600 ml-2">/week</span>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Track up to <strong>5 routes</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Full dashboard access</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">48h price predictions</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Email alerts</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-gray-400 text-xl flex-shrink-0">✗</span>
                    <span className="text-gray-400">No credit card required</span>
                  </li>
                </ul>

                <button
                  onClick={onGetStarted}
                  className="w-full py-4 bg-amber-500 text-white rounded-lg font-bold text-lg hover:bg-amber-600 transition-all shadow-lg"
                >
                  Start Free Trial
                </button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Expires after 7 days • No auto-charge
                </p>
              </div>
            </div>

            {/* Starter Agency */}
            <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden">
              <div className="p-8">
                <h3 className="text-3xl font-black text-gray-900 mb-2">Starter Agency</h3>
                <p className="text-gray-600 mb-6">For growing travel agencies</p>

                <div className="mb-6">
                  <span className="text-6xl font-black text-gray-900">$199</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start space-x-3">
                    <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Track up to <strong>20 routes</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Automatic price alerts</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Priority email support</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700">Weekly reports</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-gray-400 text-xl flex-shrink-0">✗</span>
                    <span className="text-gray-400">No API access</span>
                  </li>
                </ul>

                <button
                  onClick={onGetStarted}
                  className="w-full py-4 bg-gray-900 text-white rounded-lg font-bold text-lg hover:bg-gray-800 transition-all"
                >
                  Subscribe Now
                </button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Billed monthly via Stripe
                </p>
              </div>
            </div>

            {/* Pro Partner */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border-2 border-gray-700 overflow-hidden">
              <div className="p-8">
                <h3 className="text-3xl font-black text-white mb-2">Pro Partner</h3>
                <p className="text-gray-400 mb-6">Enterprise API integration</p>

                <div className="mb-6">
                  <span className="text-6xl font-black text-white">$499</span>
                  <span className="text-gray-400 ml-2">/month</span>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-300"><strong>Unlimited routes</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-300">24/7 automated monitoring</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-300"><strong className="text-amber-500">Full RESTful API access</strong></span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-300">Dedicated account manager</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-amber-500 text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-300">Custom integration support</span>
                  </li>
                </ul>

                <button
                  onClick={() => alert('Contact Sales: sales@faresight.com')}
                  className="w-full py-4 bg-amber-500 text-white rounded-lg font-bold text-lg hover:bg-amber-600 transition-all shadow-lg"
                >
                  Contact Sales
                </button>

                <p className="text-center text-sm text-gray-400 mt-4">
                  Custom contracts available
                </p>
              </div>
            </div>
          </div>

          {/* Money-Back Guarantee Banner */}
          <div className="mt-16 max-w-4xl mx-auto bg-amber-50 border-2 border-amber-500 rounded-xl p-6">
            <div className="flex items-center justify-center space-x-4">
              <span className="text-amber-500 text-4xl">⭐</span>
              <div>
                <h4 className="text-2xl font-black text-gray-900">100% Money-Back Guarantee</h4>
                <p className="text-gray-700">If our predictions don't save you money within 30 days, get a full refund. No questions asked.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gray-50 py-28">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-black text-center text-gray-900 mb-20">
            Built for Travel Professionals
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📊</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">ARIMA Time Series Model</h3>
                <p className="text-gray-600">
                  Advanced statistical forecasting analyzes historical patterns to predict future price movements with 92% accuracy.
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🔔</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Automatic Alerts</h3>
                <p className="text-gray-600">
                  Get notified instantly when prices are predicted to drop by 10% or more in the next 48 hours.
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⚡</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Updates</h3>
                <p className="text-gray-600">
                  Hourly prediction updates ensure you never miss the optimal booking window for your clients.
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">💼</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">B2B API Access</h3>
                <p className="text-gray-600">
                  RESTful API with token authentication for seamless integration into your existing booking systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-40" style={{ background: 'linear-gradient(to bottom right, #0b1120, #172554)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Ready to Save on Every Flight?
          </h2>
          <p className="text-2xl text-gray-300 mb-12">
            Join travel professionals using AI to predict price drops and maximize savings.
          </p>
          <button
            onClick={onGetStarted}
            className="px-12 py-6 bg-amber-500 text-white rounded-lg font-bold text-xl hover:bg-amber-600 transition-all shadow-2xl"
          >
            Get Started - It's Free
          </button>
          <div className="mt-8 flex items-center justify-center space-x-6 text-gray-400">
            <div className="flex items-center space-x-2">
              <span className="text-amber-500 text-2xl">⭐</span>
              <span className="font-semibold">100% Money-Back Guarantee</span>
            </div>
            <span className="text-gray-600">•</span>
            <span className="font-semibold">No Credit Card Required</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-xl font-bold">✈</span>
            </div>
            <span className="text-white text-xl font-bold">FareSight</span>
          </div>
          <p className="text-gray-400 text-sm">
            © 2025 FareSight. Intelligent Flight Price Predictions for B2B Travel Resellers.
          </p>
        </div>
      </div>
    </div>
  );
}

// ==================== Auth Component (Login/Signup with Supabase) ====================
function Auth({ onLogin }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        const { user, session } = await signUp(email, password, { company_name: companyName });
        setSuccess('Account created! Please check your email to verify your account.');
        console.log('[FareSight] User signed up:', user);

        // Clear form
        setEmail('');
        setPassword('');
        setCompanyName('');

        // Optionally switch to login after success
        setTimeout(() => {
          setIsSignUp(false);
          setSuccess('');
        }, 3000);
      } else {
        // Sign in
        const { session, user } = await signIn(email, password);
        console.log('[FareSight] User signed in:', user);
        console.log('[FareSight] Session:', session);

        // Store access token for backend API calls
        localStorage.setItem('faresight_supabase_token', session.access_token);
        onLogin(session.access_token);
      }
    } catch (err) {
      console.error('[FareSight] Auth error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-2xl font-bold">✈</span>
            </div>
            <h1 className="text-3xl font-bold text-blue-900">FareSight</h1>
          </div>
          <p className="text-gray-600">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="ABC Travel Agency"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isSignUp ? "Min. 6 characters" : "Your password"}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-colors disabled:opacity-50"
          >
            {loading ? (isSignUp ? 'Creating account...' : 'Signing in...') : (isSignUp ? 'Sign Up' : 'Sign In')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
              setSuccess('');
            }}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>

        {!isSignUp && (
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                // TODO: Add password reset flow
                alert('Password reset feature coming soon!');
              }}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Airport/City Mapping ====================
const AIRPORT_MAPPING = {
  // Korean Cities
  'seoul': 'ICN',
  'incheon': 'ICN',
  'gimpo': 'GMP',
  'jeju': 'CJU',
  'busan': 'PUS',
  'pusan': 'PUS',
  'daegu': 'TAE',

  // International Cities
  'tokyo': 'NRT',
  'narita': 'NRT',
  'haneda': 'HND',
  'osaka': 'KIX',
  'los angeles': 'LAX',
  'la': 'LAX',
  'new york': 'JFK',
  'nyc': 'JFK',
  'san francisco': 'SFO',
  'sf': 'SFO',
  'london': 'LHR',
  'paris': 'CDG',
  'hong kong': 'HKG',
  'singapore': 'SIN',
  'bangkok': 'BKK',
  'taipei': 'TPE',
  'manila': 'MNL',
  'shanghai': 'PVG',
  'beijing': 'PEK',
};

// Convert city name or airport code to proper airport code
const normalizeAirportCode = (input) => {
  if (!input) return '';

  const cleaned = input.trim().toUpperCase();

  // If it's already a 3-letter airport code, return it
  if (/^[A-Z]{3}$/.test(cleaned)) {
    return cleaned;
  }

  // Check if it's a city name
  const lowerInput = input.trim().toLowerCase();
  if (AIRPORT_MAPPING[lowerInput]) {
    return AIRPORT_MAPPING[lowerInput];
  }

  // Return as-is if no mapping found
  return cleaned;
};

// ==================== Add Route Modal Component ====================
function AddRouteModal({ isOpen, onClose, onRouteAdded, editingRoute }) {
  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    start_date: '',
    end_date: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (editingRoute) {
      setFormData({
        origin: editingRoute.origin || '',
        destination: editingRoute.destination || '',
        start_date: editingRoute.start_date || '',
        end_date: editingRoute.end_date || '',
      });
    } else {
      setFormData({
        origin: '',
        destination: '',
        start_date: '',
        end_date: '',
      });
    }
  }, [editingRoute, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Normalize airport codes (convert city names to codes)
      const normalizedData = {
        ...formData,
        origin: normalizeAirportCode(formData.origin),
        destination: normalizeAirportCode(formData.destination),
      };

      if (editingRoute) {
        // Update existing route
        await apiClient.patch(`/routes/${editingRoute.id}/`, normalizedData);
      } else {
        // Create new route
        await apiClient.post('/routes/', {
          ...normalizedData,
          is_active: true
        });
      }

      // Success - close modal and refresh routes
      onRouteAdded();
      onClose();

      // Reset form
      setFormData({
        origin: '',
        destination: '',
        start_date: '',
        end_date: '',
      });
    } catch (err) {
      console.error('[FareSight] Route save error:', err);
      setError(err.response?.data?.error || err.message || `Failed to ${editingRoute ? 'update' : 'create'} route`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl border-t-4 border-amber-500">
        {/* Modal Header */}
        <div className="px-8 py-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingRoute ? 'Edit Route' : 'Add New Route'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {editingRoute ? 'Update your route tracking information' : 'Track flight prices for your desired route'}
          </p>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-8 py-6">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-800 text-sm font-medium">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Origin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Origin Airport / City ✈️
              </label>
              <input
                type="text"
                name="origin"
                value={formData.origin}
                onChange={handleChange}
                placeholder="Seoul, Jeju, ICN, GMP..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Type city name (e.g., "Seoul", "Jeju") or airport code (ICN, GMP, CJU)
              </p>
            </div>

            {/* Destination */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination Airport / City 🛬
              </label>
              <input
                type="text"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                placeholder="Los Angeles, Tokyo, LAX, NRT..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Type city name or airport code - we'll auto-convert to proper code
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> We'll monitor this route and send you alerts when we predict a price drop of 10% or more within the next 48 hours.
            </p>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span>{editingRoute ? 'Updating...' : 'Adding...'}</span>
                </>
              ) : (
                <>
                  <span>{editingRoute ? '✏️' : '➕'}</span>
                  <span>{editingRoute ? 'Update Route' : 'Add Route'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== Delete Confirmation Modal ====================
function DeleteConfirmModal({ isOpen, onClose, onConfirm, routeName }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md border-t-4 border-red-500">
        <div className="px-6 py-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="text-4xl">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900">Delete Route</h2>
          </div>

          <p className="text-gray-700 mb-2">
            Are you sure you want to delete this route?
          </p>
          <p className="text-sm font-medium text-gray-900 bg-gray-100 px-3 py-2 rounded mb-4">
            {routeName}
          </p>
          <p className="text-sm text-gray-600 mb-6">
            This action cannot be undone. All prediction history for this route will be lost.
          </p>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <span>🗑️</span>
              <span>Delete Route</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Dashboard Component ====================
function Dashboard({ onNavigate, onLogout }) {
  const [routes, setRoutes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, alertsRes, subscriptionRes] = await Promise.all([
        apiClient.get('/routes/'),
        apiClient.get('/alerts/'),
        apiClient.get('/auth/subscription/status').catch(() => ({ data: null })),
      ]);

      setRoutes(routesRes.data.results || routesRes.data || []);
      setAlerts(alertsRes.data.results || alertsRes.data || []);
      setSubscriptionStatus(subscriptionRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem('faresight_supabase_token');
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('[FareSight] Logout error:', error);
      // Force logout even if Supabase signOut fails
      localStorage.removeItem('faresight_supabase_token');
      if (onLogout) {
        onLogout();
      }
    }
  };

  const handleDeleteClick = (route) => {
    setRouteToDelete(route);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!routeToDelete) return;

    try {
      await apiClient.delete(`/routes/${routeToDelete.id}/`);

      // Refresh routes list
      await fetchData();

      // Close modal
      setShowDeleteModal(false);
      setRouteToDelete(null);
    } catch (err) {
      console.error('[FareSight] Route deletion error:', err);
      setError(err.response?.data?.error || 'Failed to delete route');
      setShowDeleteModal(false);
      setRouteToDelete(null);
    }
  };

  const handleEditClick = (route) => {
    setEditingRoute(route);
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-primary-600 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-primary-700">FareSight</h1>
            <p className="text-sm text-gray-600">B2B Price Intelligence Platform</p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Subscription Badge */}
            {subscriptionStatus && (
              <div className="flex items-center space-x-3">
                <div className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 ${
                  subscriptionStatus.subscription_tier === 'pro'
                    ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white'
                    : subscriptionStatus.subscription_tier === 'starter'
                    ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                    : 'bg-gradient-to-br from-green-500 to-green-600 text-white'
                }`}>
                  <span>{
                    subscriptionStatus.subscription_tier === 'pro' ? '💎' :
                    subscriptionStatus.subscription_tier === 'starter' ? '🚀' : '🎁'
                  }</span>
                  <span>{subscriptionStatus.tier_display || 'Free Trial'}</span>
                </div>

                {/* Route Limit Badge */}
                <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                  {routes.length} / {subscriptionStatus.max_routes === 999999 ? '∞' : subscriptionStatus.max_routes} routes
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-gray-700 hover:text-primary-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Route Limit Warning */}
        {subscriptionStatus && routes.length >= subscriptionStatus.max_routes && subscriptionStatus.subscription_tier !== 'pro' && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-2xl">⚠️</span>
                  <h3 className="text-lg font-bold text-amber-900">
                    Route Limit Reached
                  </h3>
                </div>
                <p className="text-amber-800 mb-1">
                  You've reached your route limit ({subscriptionStatus.max_routes} routes)
                </p>
                <p className="text-amber-700 text-sm">
                  {subscriptionStatus.subscription_tier === 'free_trial'
                    ? 'Upgrade to Starter Agency to track up to 20 routes, or Pro Partner for unlimited routes.'
                    : 'Upgrade to Pro Partner for unlimited route tracking and full API access.'}
                </p>
              </div>
              <button
                onClick={() => {/* TODO: Open upgrade modal */}}
                className="ml-4 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-colors whitespace-nowrap"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}

        {/* Alert Summary Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-accent-green to-emerald-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Active Alerts</p>
                <p className="text-4xl font-bold mt-2">{alerts.length}</p>
              </div>
              <div className="text-5xl opacity-75">🔔</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Tracked Routes</p>
                <p className="text-4xl font-bold mt-2">{routes.length}</p>
              </div>
              <div className="text-5xl opacity-75">✈️</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-accent-gold to-amber-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium opacity-90">Money-Back Guarantee</p>
                <p className="text-2xl font-bold mt-2">100%</p>
              </div>
              <div className="text-5xl opacity-75">💰</div>
            </div>
          </div>
        </div>

        {/* Add/Edit Route Modal */}
        <AddRouteModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setEditingRoute(null);
          }}
          onRouteAdded={fetchData}
          editingRoute={editingRoute}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRouteToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          routeName={routeToDelete ? `${routeToDelete.origin} → ${routeToDelete.destination}` : ''}
        />

        {/* Routes Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Your Tracked Routes</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-colors flex items-center space-x-2"
            >
              <span>➕</span>
              <span>Add New Route</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Travel Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {routes.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                      No routes tracked yet. Add a route to start monitoring prices.
                    </td>
                  </tr>
                ) : (
                  routes.map((route) => (
                    <tr key={route.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {route.origin} → {route.destination}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {route.start_date} to {route.end_date}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {route.is_active ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => onNavigate('detail', route.id)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            📊 View
                          </button>
                          <button
                            onClick={() => handleEditClick(route)}
                            className="text-amber-600 hover:text-amber-900 font-medium"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(route)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Alerts */}
        {alerts.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">Recent Price Alerts</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {alerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {alert.route_origin} → {alert.route_destination}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Predicted drop: {alert.predicted_drop_percent}%
                        (${alert.current_price} → ${alert.predicted_price})
                      </p>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(alert.triggered_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ==================== Route Detail Component ====================
function RouteDetail({ routeId, onNavigate, onLogout }) {
  const [route, setRoute] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRouteDetail();
  }, [routeId]);

  const fetchRouteDetail = async () => {
    try {
      const [routeRes, predictionRes] = await Promise.all([
        apiClient.get(`/routes/${routeId}/`),
        apiClient.get(`/routes/${routeId}/predict`),
      ]);

      setRoute(routeRes.data);
      setPrediction(predictionRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate price history chart data with actual dates
  const generateChartData = () => {
    if (!prediction || !prediction.prediction) return [];

    const data = [];
    const currentPrice = prediction.prediction.current_price;
    const predictedPrice = prediction.prediction.predicted_lowest_price;
    const today = new Date();

    // 30 historical data points (past 30 days)
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - (29 - i));
      const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
      const variance = (Math.random() - 0.5) * 40;

      data.push({
        day: i - 29,
        date: dateStr,
        fullDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        price: parseFloat((currentPrice + variance + (i * 2)).toFixed(2)),
        type: 'historical',
        isToday: false,
      });
    }

    // Today's current price (day 0)
    const todayStr = `${today.getMonth() + 1}/${today.getDate()}`;
    data.push({
      day: 0,
      date: todayStr,
      fullDate: 'Today',
      price: parseFloat(currentPrice.toFixed(2)),
      type: 'current',
      isToday: true,
    });

    // Tomorrow (day 1) - predicted
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = `${tomorrow.getMonth() + 1}/${tomorrow.getDate()}`;
    const day1Price = parseFloat(((currentPrice + predictedPrice) / 2).toFixed(2));

    data.push({
      day: 1,
      date: tomorrowStr,
      fullDate: 'Tomorrow',
      price: null, // Don't show historical line in prediction area
      predicted: day1Price,
      type: 'predicted',
      isToday: false,
    });

    // Day after tomorrow (day 2) - predicted lowest
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);
    const dayAfterStr = `${dayAfter.getMonth() + 1}/${dayAfter.getDate()}`;

    data.push({
      day: 2,
      date: dayAfterStr,
      fullDate: dayAfter.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      price: null,
      predicted: parseFloat(predictedPrice.toFixed(2)),
      type: 'predicted',
      isToday: false,
    });

    return data;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-primary-600 text-xl">Loading prediction...</div>
      </div>
    );
  }

  if (error || !route || !prediction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <p className="text-red-600 mb-4">{error || 'Failed to load data'}</p>
          <button
            onClick={() => onNavigate('dashboard')}
            className="text-primary-600 hover:text-primary-900"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const chartData = generateChartData();
  const recommendation = prediction.prediction.alert_needed ? 'BUY NOW' : 'MONITOR';
  const recommendationColor = prediction.prediction.alert_needed ? 'bg-accent-green' : 'bg-blue-500';

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem('faresight_supabase_token');
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('[FareSight] Logout error:', error);
      localStorage.removeItem('faresight_supabase_token');
      if (onLogout) {
        onLogout();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-start mb-2">
            <button
              onClick={() => onNavigate('dashboard')}
              className="text-primary-600 hover:text-primary-900 inline-flex items-center"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-gray-700 hover:text-primary-600 transition-colors"
            >
              Logout
            </button>
          </div>
          <h1 className="text-2xl font-bold text-primary-700">
            {route.origin} → {route.destination}
          </h1>
          <p className="text-sm text-gray-600">
            Travel: {route.start_date} to {route.end_date}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Recommendation Card */}
        <div className={`${recommendationColor} rounded-lg shadow-lg p-8 text-white mb-8`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium opacity-90 mb-2">RECOMMENDED ACTION</p>
              <p className="text-5xl font-bold">{recommendation}</p>
              {prediction.prediction.alert_needed && (
                <p className="mt-4 text-lg">
                  Predicted price drop: {prediction.prediction.drop_percent}%
                </p>
              )}
            </div>
            <div className="text-8xl opacity-75">
              {prediction.prediction.alert_needed ? '🎯' : '👀'}
            </div>
          </div>
        </div>

        {/* Price Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm font-medium text-gray-600 mb-1">Current Market Price</p>
            <p className="text-xs text-gray-500 mb-2">(As of {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})</p>
            <p className="text-3xl font-bold text-gray-900">
              ${prediction.prediction.current_price}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm font-medium text-gray-600 mb-1">Predicted Lowest Price</p>
            <p className="text-xs text-gray-500 mb-2">(Within next 48 hours)</p>
            <p className="text-3xl font-bold text-accent-green">
              ${prediction.prediction.predicted_lowest_price}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-sm font-medium text-gray-600 mb-2">Potential Savings</p>
            <p className={`text-3xl font-bold ${prediction.prediction.alert_needed ? 'text-accent-green' : 'text-gray-400'}`}>
              {prediction.prediction.drop_percent >= 0 ? prediction.prediction.drop_percent : 0}%
            </p>
          </div>
        </div>

        {/* Price History Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Price History & 48h Prediction</h2>

          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="fullDate"
                label={{ value: 'Date', position: 'insideBottom', offset: -5 }}
                stroke="#6b7280"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis
                label={{ value: 'Price ($)', angle: -90, position: 'insideLeft' }}
                stroke="#6b7280"
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
                formatter={(value, name) => {
                  if (value === null || value === undefined) return null;
                  // name is the actual name prop from Line component
                  return [`$${value.toFixed(2)}`, name];
                }}
                labelFormatter={(label) => {
                  const item = chartData.find(d => d.fullDate === label);
                  if (!item) return label;
                  if (item.isToday) return `📅 ${label} (Current)`;
                  if (item.type === 'predicted') return `🔮 ${label} (Prediction)`;
                  return `📅 ${label}`;
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />

              {/* Reference line for "Today" */}
              <ReferenceLine
                x="Today"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="5 5"
                label={{
                  value: 'TODAY',
                  position: 'top',
                  fill: '#f59e0b',
                  fontSize: 12,
                  fontWeight: 'bold'
                }}
              />

              {/* Historical price line */}
              <Line
                type="monotone"
                dataKey="price"
                stroke="#1e40af"
                strokeWidth={2}
                dot={{ r: 2 }}
                connectNulls={false}
                name="Historical Price"
              />

              {/* Predicted price line (emphasized) */}
              <Line
                type="monotone"
                dataKey="predicted"
                stroke="#10b981"
                strokeWidth={4}
                strokeDasharray="5 5"
                dot={{ r: 6, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                connectNulls={false}
                name="🔮 Predicted Price (AI)"
              />
            </LineChart>
          </ResponsiveContainer>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 text-2xl">ℹ️</div>
              <div className="flex-1">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>How to read this chart:</strong>
                </p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li><strong>Blue solid line:</strong> Historical market prices (past 30 days)</li>
                  <li><strong>Orange dashed line:</strong> Today's current price point</li>
                  <li><strong>Green dashed line:</strong> AI-predicted prices for next 48 hours</li>
                </ul>
                {prediction.prediction.alert_needed && (
                  <p className="mt-3 text-sm font-bold text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                    💡 Recommendation: Book within 48 hours to secure predicted {prediction.prediction.drop_percent}% discount!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Flight Booking Links Section - Phase 5.4 */}
        {prediction.booking_links && (
          <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ✈️ Book This Flight Now
            </h2>
            <p className="text-gray-600 mb-6">
              Compare prices across multiple booking sites and secure the best deal
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Google Flights */}
              <a
                href={prediction.booking_links.google_flights}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 py-4 rounded-lg text-center font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
              >
                <span className="text-2xl">🔍</span>
                <span>Google Flights</span>
              </a>

              {/* Kayak */}
              <a
                href={prediction.booking_links.kayak}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange-600 text-white px-6 py-4 rounded-lg text-center font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
              >
                <span className="text-2xl">✈️</span>
                <span>Kayak</span>
              </a>

              {/* Skyscanner */}
              <a
                href={prediction.booking_links.skyscanner}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white px-6 py-4 rounded-lg text-center font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
              >
                <span className="text-2xl">🌐</span>
                <span>Skyscanner</span>
              </a>

              {/* Expedia */}
              <a
                href={prediction.booking_links.expedia}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-purple-600 text-white px-6 py-4 rounded-lg text-center font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 shadow-md hover:shadow-lg"
              >
                <span className="text-2xl">🎫</span>
                <span>Expedia</span>
              </a>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">
              💡 Tip: Click multiple sites to compare prices in real-time and find the best deal
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ==================== Main App Component ====================
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing Supabase session
    const checkSession = async () => {
      try {
        const session = await getSession();
        if (session) {
          console.log('[FareSight] Found existing session');
          localStorage.setItem('faresight_supabase_token', session.access_token);
          setIsLoggedIn(true);
          setShowLanding(false);
        }
      } catch (error) {
        console.error('[FareSight] Session check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[FareSight] Auth state changed:', event);
      if (session) {
        localStorage.setItem('faresight_supabase_token', session.access_token);
        setIsLoggedIn(true);
        setShowLanding(false);
      } else {
        localStorage.removeItem('faresight_supabase_token');
        setIsLoggedIn(false);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleGetStarted = () => {
    setShowLanding(false);
  };

  const handleLogin = (token) => {
    setIsLoggedIn(true);
    setShowLanding(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowLanding(true);
    setCurrentView('dashboard');
  };

  const handleNavigate = (view, routeId = null) => {
    setCurrentView(view);
    setSelectedRouteId(routeId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show landing page first
  if (showLanding && !isLoggedIn) {
    return <LandingPage onGetStarted={handleGetStarted} />;
  }

  // Show auth (login/signup) if not logged in
  if (!isLoggedIn) {
    return <Auth onLogin={handleLogin} />;
  }

  // Show dashboard/app
  switch (currentView) {
    case 'dashboard':
      return <Dashboard onNavigate={handleNavigate} onLogout={handleLogout} />;
    case 'detail':
      return <RouteDetail routeId={selectedRouteId} onNavigate={handleNavigate} onLogout={handleLogout} />;
    default:
      return <Dashboard onNavigate={handleNavigate} onLogout={handleLogout} />;
  }
}

export default App;
